import { Button, Col, Row, Typography } from "antd";
import { notify } from "@/shared/utils/notify";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { WrapperModalBanners } from "../../../../modules/banners/ui/styled";
import {
  selectBannersForUpdate,
  selectIsUpdateBanners,
  selectModalBanners,
} from "../../../../redux";
import { DataBanner } from "../../../../redux/@types";
import {
  productCategoriesService,
  promotionRulesService,
  rulesApplyFiftyPercentDiscount,
} from "../../../services";
import { PATH_S3_BUCKET } from "../../../utils/constants";
import { InputData, InputNumber, InputSelect, InputText } from "../inputs";
import Upload from "../inputs/Upload";

const { Title } = Typography;

/** Prefijos para distinguir promotion-rules vs rollos 50% en el mismo select. */
export const PROMO_ACTION_PREFIX = {
  rule: "pr:",
  fifty: "fo:",
} as const;

interface ModalBannersProps {
  isLoading: boolean;
  changeModalBannersState: () => void;
  onSubmitCreateOrUpdate: (data: DataBanner) => Promise<void>;
}

export const ModalBanners = ({ isLoading, changeModalBannersState, onSubmitCreateOrUpdate }: ModalBannersProps) => {
  const modalAddressesUser = useSelector(selectModalBanners);
  const isUpdateBanners = useSelector(selectIsUpdateBanners);
  const updateBanners = useSelector(selectBannersForUpdate);
  const [categories, setCategories] = useState<{ value: string; title: string }[]>([]);
  const [promotions, setPromotions] = useState<{ value: string; title: string }[]>([]);

  const {
    control,
    handleSubmit: onSubmit,
    setValue,
    watch,
  } = useFormContext<DataBanner>();
  const pathMovile = watch("path_movile");
  const pathDesktop = watch("path_desktop");
  const actionType = watch("action_type");
  const type = watch("type");

  useEffect(() => {
    if (actionType === "CATEGORY" && modalAddressesUser) {
      productCategoriesService
        .find({
          query: { $select: ["id", "name"], $limit: 500 },
        })
        .then((res: any) => {
          const items = res?.data ?? [];
          setCategories(
            items.map((c: { id: number; name: string }) => ({
              value: String(c.id),
              title: c.name,
            }))
          );
        })
        .catch(() => setCategories([]));
    }

    if (actionType === "PROMOTION" && modalAddressesUser) {
      Promise.all([
        promotionRulesService.find({
          query: { $limit: 100, $sort: { createdAt: -1 } },
        }),
        rulesApplyFiftyPercentDiscount.find({
          query: { $limit: 100, $sort: { createdAt: -1 } },
        }),
      ])
        .then(([promoRes, fiftyRes]: any[]) => {
          const rules = (promoRes?.data ?? []).map(
            (p: {
              id: number;
              name: string;
              promo_type: string;
              status: string;
            }) => ({
              value: `${PROMO_ACTION_PREFIX.rule}${p.id}`,
              title: `${p.name} (${p.promo_type})${
                p.status === "inactive" ? " - INACTIVA" : ""
              }`,
            }),
          );
          const fifty = (fiftyRes?.data ?? []).map(
            (p: { id: number; name: string; status: string }) => ({
              value: `${PROMO_ACTION_PREFIX.fifty}${p.id}`,
              title: `${p.name} (50%)${
                p.status === "inactive" ? " - INACTIVA" : ""
              }`,
            }),
          );
          setPromotions([...fifty, ...rules]);
        })
        .catch(() => setPromotions([]));
    }
  }, [actionType, modalAddressesUser]);

  return (
    <WrapperModalBanners
      title={
        isUpdateBanners
          ? `Actualizando banner: '${updateBanners?.id!}'`
          : `Creando banner`
      }
      open={modalAddressesUser}
      width={520}
      placement="right"
      destroyOnClose
      onClose={changeModalBannersState}
    >
      <Row gutter={16} style={{ marginTop: "10px" }}>
        <Col span={12}>
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            Banner mobile:
          </Title>
        {pathMovile ? (
          <img
            src={`${PATH_S3_BUCKET}/${pathMovile}`}
            alt="path_movile"
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "140px",
              height: "100px",
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        ) : (
          <img
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "140px",
              height: "100px",
              objectFit: "cover",
              borderRadius: 6,
            }}
            alt="placeholder"
            src="data:image/png;base64,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"
          />
        )}
        <Upload
          filePath="bannerMovile"
          onUploadedFile={(file) => {
            setValue("path_movile", file.fileKey);
            notify.success("Se ha cargado el banner móvil");
          }}
        />
        </Col>
        <Col span={12}>
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            Banner desktop:
          </Title>
        {pathDesktop ? (
          <img
            src={`${PATH_S3_BUCKET}/${pathDesktop}`}
            alt="path_desktop"
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "140px",
              height: "100px",
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        ) : (
          <img
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "140px",
              height: "100px",
              objectFit: "cover",
              borderRadius: 6,
            }}
              alt="placeholder"
              src="data:image/png;base64,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"
            />
        )}
        <Upload
          filePath="bannerDesktop"
          onUploadedFile={(file) => {
            setValue("path_desktop", file.fileKey);
            notify.success("Se ha cargado el banner desktop");
          }}
        />
        </Col>
      </Row>
      <Col
        span={24}
        style={{
          display: "flex",
          marginTop: "10px",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Prioridad:
        </Title>
        <InputNumber
          //   stringMode={true}
          placeHolder="8"
          name="priority"
          control={control}
          min
        />
      </Col>
      <Col
        span={24}
        style={{
          display: "flex",
          marginTop: "10px",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Estado:
        </Title>
        <InputSelect
          name="status"
          control={control}
          dataOptions={[
            { title: "Activo", value: "active" },
            { title: "Inactivo", value: "inactive" },
          ]}
          label=""
        />
      </Col>
      <Col
        span={24}
        style={{
          display: "flex",
          marginTop: "10px",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Tipo:
        </Title>
        <InputSelect
          name="type"
          control={control}
          dataOptions={[
            { title: "HOME WEB", value: "HOME" },
            { title: "HOME APP", value: "HOME_MOBILE" },
            { title: "OFERTAS", value: "OFFERS" },
            { title: "POPUP", value: "POPUP" },
            { title: "ACADEMIA", value: "ACADEMY" },
            { title: "OFERTAS RELAMPAGO", value: "FLASH_SALE" },
          ]}
          label=""
        />
      </Col>
      <Col
        span={24}
        style={{
          display: "flex",
          marginTop: "10px",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Tipo de acción:
        </Title>
        <InputSelect
          name="action_type"
          control={control}
          dataOptions={[
            { title: "BANNER DE PRODUCTOS", value: "BANNER_PRODUCTS" },
            { title: "CATEGORIA", value: "CATEGORY" },
            { title: "PRODUCTO", value: "PRODUCT" },
            { title: "PROMOCIÓN", value: "PROMOTION" },
            { title: "URL", value: "URL" },
            { title: "CURSO", value: "COURSE" },
          ]}
          label=""
        />
      </Col>
      {isUpdateBanners && actionType === "URL" && (
        <Col
          span={24}
          style={{
            display: "flex",
            marginTop: "10px",
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            URL:
          </Title>
          <InputText
            placeHolder="https://www.example.com"
            name="url"
            control={control}
            rows
          />
        </Col>
      )}
      {isUpdateBanners && type === "FLASH_SALE" && (
        <Col
          span={24}
          style={{
            display: "flex",
            marginTop: "10px",
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            Fecha de inicio:
          </Title>
          <InputData
            dateFormat="YYYY-MM-DD HH:mm:ss"
            placeHolder="29/10/2022 00:00:00"
            name="start_date"
            control={control}
          />
        </Col>
      )}
      {isUpdateBanners && type === "FLASH_SALE" && (
        <Col
          span={24}
          style={{
            display: "flex",
            marginTop: "10px",
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            Fecha de finalización:
          </Title>
          <InputData
            dateFormat="YYYY-MM-DD HH:mm:ss"
            placeHolder="29/10/2022 00:00:00"
            name="end_date"
            control={control}
          />
        </Col>
      )}
      {actionType !== "URL" && actionType && (
        <Col
          span={24}
          style={{
            display: "flex",
            marginTop: "10px",
            padding: 5,
            justifyContent: "center",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            {actionType === "CATEGORY"
              ? "Categoría:"
              : actionType === "PROMOTION"
              ? "Promoción:"
              : "ID de acción:"}
          </Title>
          {actionType === "CATEGORY" ? (
            <InputSelect
              name="action_id"
              control={control}
              dataOptions={categories}
              label=""
              placeholder="Seleccionar categoría"
              showSearch
              isClearable
            />
          ) : actionType === "PROMOTION" ? (
            <InputSelect
              name="action_id"
              control={control}
              dataOptions={promotions}
              label=""
              placeholder="Seleccionar promoción"
              showSearch
              isClearable
            />
          ) : (
            <InputNumber placeHolder="1360" name="action_id" control={control} />
          )}
        </Col>
      )}
      <Button
        style={{
          width: "100%",
          marginTop: 20,
          borderRadius: "8px",
          height: "44px",
          fontWeight: 500,
          fontSize: "15px",
        }}
        type="primary"
        size="large"
        onClick={onSubmit(onSubmitCreateOrUpdate)}
        loading={isLoading}
      >
        {isUpdateBanners ? "Actualizar información" : "Crear banner"}
      </Button>
    </WrapperModalBanners>
  );
};
